import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

// local imports
import calendarLoading from 'assets/icons/loading-calendar.png'
import { IDoctorFilterForm, IFilteredDoctorList } from 'types/HealthType'
import useDidMountEffect from 'utils/useDidMountEffect'
import DateUtil from 'utils/DateUtil'
import AuthUtil from 'utils/AuthUtil'
import { useGetDoctorList, useGetLocationList, useGetDoctorSpecialtyList, useAppointmentModal } from '../hooks'
import Loader from 'components/ui/Loader'
import NoContent from 'components/ui/NoContent'
import NotificationDialog from 'components/ui/NotificationDialog'
import DoctorCard from './DoctorCard'
import DoctorFilterForm from './DoctorFilterForm'
import DoctorAndSpecialtySearch from './DoctorAndSpecialtySearch'
import AppointmentModalWrapper from '../appointment-scheduler/AppointmentModal'

// assets
import successImage from 'assets/images/success-image.svg'

/* filtered date - default */
const today = DateUtil.getStartOfDay(new Date())

export default function DoctorList() {
  const { t } = useTranslation()
  const history = useHistory()
  const [locationIds, setLocationIds] = useState<number[] | []>([])

  /* Get Current logged employee infos - (location id) */
  const { data: locationList, isLoading } = useGetLocationList()

  useEffect(() => {
    const employeeLocation = AuthUtil.getEmployeeSummary()?.locationId || 0
    const empLocationIsExist = locationList?.map((o: any) => o.id).includes(employeeLocation)
    setLocationIds(() => (empLocationIsExist ? [employeeLocation] : []))
    setValue('locationIds', empLocationIsExist ? [employeeLocation] : [])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationList])

  /* Filter form default values */
  const defaultValues: IDoctorFilterForm = { locationIds: locationIds, specialtyId: 0, reservationDate: today }

  /*  Search and filter from doctor list */
  const [searchText, setSearchText] = useState('')
  const [filteredDoc, setFilteredDoc] = useState<IFilteredDoctorList[] | []>([])

  /* Filter form state  */
  const { control, reset, handleSubmit, formState, watch, setValue } = useForm<IDoctorFilterForm>({
    defaultValues,
  })

  /* Get doctor specialties by selected location id */
  const filteredDate = watch('reservationDate') || today
  const _locationIds = watch('locationIds')

  /* Reset doctor specialties when location is changed. */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setValue('specialtyId', 0), [_locationIds])

  /* Get Doctor List Get Doctors Specialties - Locations | Custom Hooks */
  const { getDoctorList, doctors, loading } = useGetDoctorList()
  const { data: specialtyList } = useGetDoctorSpecialtyList(0, true, locationIds)

  /* Form Actions | Methods | button disabled states */
  const { isDirty, submitCount } = formState
  const disableResetBtn = !isDirty && submitCount < 1
  const disableSubmitBtn = !isDirty && submitCount < 1

  /* Handle Form Submit */
  const onSubmit = (data: IDoctorFilterForm) => getDoctorList(data)

  /* Handle Reset Filter Form */
  const handleResetForm = () => {
    reset(
      { locationIds: locationIds, reservationDate: today },
      { keepDirtyValues: false, keepDirty: false, keepDefaultValues: false }
    )
    !!submitCount && onSubmit(defaultValues)
    setSearchText('')
  }

  /* Get Doctors List on initial page load  */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useDidMountEffect(() => onSubmit(defaultValues), [isLoading])

  /* Render doctors or filtered doctors list */
  const renderList = searchText ? filteredDoc : doctors

  /* Appointment Modal (Open - Close, Selected Doctor). | Custom Hook */
  const {
    openModal,
    closeModal,
    modal: isOpen,
    selectedDoc,
    setSelectedDoc,
    notification,
    setNotification,
  } = useAppointmentModal()

  /* Handle Loading Status */
  if (loading || isLoading) return <ListLoader />

  return (
    <div className="campus-services">
      {/* Filter Form */}
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormLayoutWrapper>
          <DoctorAndSpecialtySearch
            doctors={doctors}
            setData={setFilteredDoc}
            searchText={searchText}
            setSearchText={setSearchText}
          />
          <Line className="pi pi-ellipsis-h" />
          <DoctorFilterForm
            control={control}
            specialtyList={specialtyList}
            locationList={locationList}
            disableResetBtn={disableResetBtn}
            disableSubmitBtn={disableSubmitBtn}
            handleReset={handleResetForm}
          />
        </FormLayoutWrapper>
      </form>

      <Divider />

      {/* Card List */}
      {loading ? (
        <div className="py-5 my-4">
          <Loader src={calendarLoading} />
        </div>
      ) : (
        <DoctorCardList>
          {!!renderList?.length ? (
            renderList?.map((doc) => <DoctorCard openModal={openModal} key={doc.id} doctor={doc} />)
          ) : (
            <NoContent />
          )}
        </DoctorCardList>
      )}

      {/* Doctor Appointment Modal */}
      {!!selectedDoc ? (
        <AppointmentModalWrapper
          doctors={renderList}
          open={isOpen}
          openModal={openModal}
          closeModal={closeModal}
          showNotification={() => setNotification(true)}
          setSelectedDoc={setSelectedDoc}
          selectedDoc={selectedDoc}
          filterDate={filteredDate}
        />
      ) : null}

      <NotificationDialog
        display={notification}
        imgSrc={successImage}
        goHomeButton={true}
        title={t('appointment-modal.notification-title')}
        content={t('appointment-modal.notification-text')}
        firstButtonLabel={t('buttons.go-to-calendar')}
        onHide={() => setNotification(false)}
        onSubmit={() => history.push('/calendar')}
        selectedType={null}
        setSelectedType={undefined}
        types={[]}
      />
    </div>
  )
}

function ListLoader() {
  return (
    <div className="py-5 my-4">
      <Loader src={calendarLoading} />
    </div>
  )
}

const Divider = styled.div`
  border: 1px solid #cecece;
`
const Line = styled.span`
  font-size: 1.3rem;
  color: #9d9d9d;

  @media (max-width: 1400px) {
    display: none;
  }
`
const FormLayoutWrapper = styled.div`
  display: flex;
  // justify-content: space-between;
  // flex-wrap: wrap;
  align-items: center;
  gap: 14px;
  margin-top: 22px;
  margin-bottom: 1rem;
  padding: 0 0.5rem;
`
const DoctorCardList = styled.div`
  display: flex;
  max-height: 700px;
  overflow-y: auto;
  padding: 1rem 0.25rem 0.25rem;
  // justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 16px;

  &::-webkit-scrollbar-track {
    -webkit-box-shadow: none !important;
    background-color: #d3d3d3 !important;
    border-radius: 10px;
  }

  &::-webkit-scrollbar {
    height: 6px;
    width: 6px !important;
    background-color: #d3d3d3;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #7c7c7c;
    border-radius: 10px;
  }
`
